import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DeleteOutline18Icon,
  EditOutline18Icon,
  SpeechBubbleOutline18Icon,
  WagonFill24Icon,
} from "@vygruppen/spor-icon-react";
import { getBackendUrl } from "api/common";
import { mutationFnDELETE } from "api/tanStackQuery/helpers";
import { checklistQueryKey } from "features/CenterContent/RoleContent/VehicleChecklist/Checklist";
import {
  AccordionPanelStyled,
  ExpandableHeader,
  ExpandableStyled,
} from "features/CenterContent/RoleContent/VehicleChecklist/ChecklistCustomStyle";
import ChecklistHistoryEntry from "features/CenterContent/RoleContent/VehicleChecklist/ChecklistHistoryEntry";
import ChecklistModal from "features/CenterContent/RoleContent/VehicleChecklist/ChecklistModal";
import DeleteChecklistModal from "features/CenterContent/RoleContent/VehicleChecklist/DeleteChecklistModal";
import { Employee } from "features/CenterContent/RoleContent/VehicleChecklist/hooks/useEmployeeSearch";
import { FillOut } from "features/CenterContent/RoleContent/VehicleChecklist/schema/responseSchema";
import {
  formatDate,
  formatTime,
  getChecklistText,
} from "features/CenterContent/RoleContent/VehicleChecklist/utils";
import { VehicleSetVariant } from "features/CenterContent/RoleContent/VehicleChecklist/VehicleSetVariant";
import { FC, useCallback, useMemo, useState } from "react";
import { Box, Button, Flex, Grid, Text } from "shared/components/Spor";
import { useDropsRole } from "stores/useDropsRole";
import { useTheme } from "styled-components";

type ChecklistTableRowProps = {
  checklist: FillOut;
  vehicleSetVariants: VehicleSetVariant[];
  employees: Employee[];
  refetch: () => void;
};

const ChecklistTableRow: FC<ChecklistTableRowProps> = ({
  checklist,
  vehicleSetVariants,
  employees,
  refetch,
}) => {
  const theme = useTheme();
  const { isLokleder } = useDropsRole();
  const queryClient = useQueryClient();

  const { mutate: deleteChecklist } = useMutation({
    mutationFn: () =>
      mutationFnDELETE<undefined>(
        `${getBackendUrl()}/checklist/fill-out/${checklist.fillOutId}`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: checklistQueryKey() });
    },
  });

  const [modalState, setModalState] = useState<"edit" | "delete" | "closed">(
    "closed",
  );

  const [[, firstVersion], ...checklistHistory] = useMemo(
    () => (checklist.history ? Object.entries(checklist.history) : []),
    [checklist.history],
  );

  const handleDeleteClick = useCallback(() => {
    setModalState("delete");
  }, []);

  const handleEditClick = useCallback(() => {
    setModalState("edit");
  }, []);

  const handleModalClose = useCallback(() => {
    setModalState("closed");
    refetch();
  }, []);

  const expandableHeader = (
    <ExpandableHeader>
      <div>
        <Text>{formatDate(checklist.executedAt.toString())}</Text>
        <Text fontWeight={500} color={theme.colorTextSecondary}>
          {formatTime(checklist.executedAt.toString())}
        </Text>
      </div>

      {checklistHistory.length ? (
        <div>
          <Text>{formatDate(checklist.changedAt.toString())}</Text>
          <Text fontWeight={500} color={theme.colorTextSecondary}>
            {formatTime(checklist.changedAt.toString())}
          </Text>
        </div>
      ) : (
        <div>–</div>
      )}

      <Text>{getChecklistText(checklist.checklistType)}</Text>
      <Flex alignItems="center" gap="6px">
        <WagonFill24Icon />
        <Text>{checklist.rollingStock ? checklist.rollingStock : null}</Text>
      </Flex>

      <Text fontWeight={500}>{checklist.executorName ?? "–"}</Text>

      {checklistHistory.length ? (
        <Text fontWeight={500}>{checklist.changedByName}</Text>
      ) : (
        <Text ml="6px">–</Text>
      )}

      <Flex justifyContent="end">
        {firstVersion.comment ||
        checklistHistory.some(([, entry]) => entry.comment) ? (
          <SpeechBubbleOutline18Icon mr="50%" />
        ) : null}
      </Flex>
    </ExpandableHeader>
  );

  return (
    <ExpandableStyled title={expandableHeader} collapsible>
      <AccordionPanelStyled overflow="visible">
        {firstVersion?.comment ? (
          <Grid templateColumns="repeat(7, 1fr)" margin="1rem 0" gap="6px">
            <Text fontWeight="bold" gridColumn="2">
              Kommentar:
            </Text>
            <Text gridColumn="3 / 7">{firstVersion.comment}</Text>
          </Grid>
        ) : (
          <Box height="12px" />
        )}

        {checklistHistory.map(([versionKey, currentEntry], index, array) => (
          <ChecklistHistoryEntry
            key={versionKey}
            currentEntry={currentEntry}
            previousEntry={index === 0 ? firstVersion : array[index - 1][1]}
            index={index}
          />
        ))}

        {isLokleder() && (
          <Flex justifyContent="flex-end">
            <Button
              aria-label="Slett kvittering"
              variant="ghost"
              size="sm"
              onClick={handleDeleteClick}
              leftIcon={<DeleteOutline18Icon />}
            >
              Slett kvittering
            </Button>

            <Button
              aria-label="Rediger kvittering"
              variant="tertiary"
              size="sm"
              leftIcon={<EditOutline18Icon />}
              onClick={handleEditClick}
            >
              Endre
            </Button>
          </Flex>
        )}

        {modalState === "edit" && (
          <ChecklistModal
            vehicleSetVariants={vehicleSetVariants}
            employees={employees}
            onClose={handleModalClose}
            currentFillOut={checklist}
            refetch={refetch}
          />
        )}

        {modalState === "delete" && (
          <DeleteChecklistModal
            onClose={handleModalClose}
            currentFillOut={checklist}
            handleDelete={deleteChecklist}
            refetch={refetch}
          />
        )}
      </AccordionPanelStyled>
    </ExpandableStyled>
  );
};

export default ChecklistTableRow;
